/**
 * PlantasIotDashboard.jsx  v9
 * Dashboard multi-planta (base plantas_iot)
 * v2: click en tarjeta abre PlantasIotTankHistoryModal
 * v3: click en el strip abre PlantasIotStripHistoryModal
 * v4: click en "Alarmas 24 h" abre PlantasIotAlarmasModal
 * v5: vista "Histórico" (PlantasIotHistoryPanel)
 * v6: engranaje de configuración (permiso PlantasIot / configuracion)
 * v7: tarjetas estilo Alvear (PlantasIotTankCard) + nombres y umbrales configurados
 * v8: envases equivalentes en las cajas de Propano y Butano
 * v9: envases calculados sobre lo que hay por encima del 10% de cada tanque
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Alert, Box, Chip, CircularProgress, Divider, FormControl, Grid, IconButton,
    MenuItem, Paper, Select, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip,
    Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    Dashboard as DashboardIcon,
    Settings as SettingsIcon,
    Wifi as WifiIcon,
    WifiOff as WifiOffIcon,
    Water as WaterIcon,
    CloudOff as CloudOffIcon,
} from '@mui/icons-material';

import Navbar from '../../../../Navbar';
import { fetchPermisos } from '../../../../../services/services';
import PlantasIotTankCard from './PlantasIotTankCard';
import PlantasIotTankHistoryModal from './PlantasIotTankHistoryModal';
import PlantasIotStripHistoryModal from './PlantasIotStripHistoryModal';
import PlantasIotAlarmasModal from './PlantasIotAlarmasModal';
import PlantasIotHistoryPanel from './PlantasIotHistoryPanel';
import PlantasIotConfigDialog from './PlantasIotConfigDialog';

const POLL_MS = 30000;

const COLOR_PRODUCTO = { propano: '#38BDF8', butano: '#FB923C' };
const colorProducto = (p) => COLOR_PRODUCTO[(p || '').toLowerCase()] || '#94A3B8';

// Envases que se llenan con cada producto (kg)
const ENVASES_KG = {
    butano: [10, 15],
    propano: [15, 45],
};

// Nivel mínimo que queda en el tanque y no se envasa (% del volumen)
const PISO_ENVASADO_PCT = 10;

function fmtFecha(iso) {
    return iso ? new Date(iso).toLocaleString('es-AR', { hour12: false }) : '—';
}

function fmtNum(v, dec = 1) {
    if (v === null || v === undefined || isNaN(v)) return '—';
    return Number(v).toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Kg envasables de un tanque: lo que hay por encima del piso, con la densidad actual
function kgEnvasables(t) {
    const pct = t.volumen_pct;
    if (pct === null || pct === undefined || t.masa_kg === null || t.masa_kg === undefined) return 0;
    if (pct <= PISO_ENVASADO_PCT) return 0;
    return t.masa_kg * (pct - PISO_ENVASADO_PCT) / pct;
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function PlantasIotDashboard({ apiUrl = 'http://localhost:3001' }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [view, setView] = useState('realtime');
    const [plantas, setPlantas] = useState([]);
    const [plantaId, setPlantaId] = useState('');
    const [tanques, setTanques] = useState([]);
    const [alarmas24h, setAlarmas24h] = useState(0);
    const [errorServidor, setErrorServidor] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [recarga, setRecarga] = useState(0);   // se incrementa para forzar recarga

    // Permisos
    const [permisos, setPermisos] = useState([]);
    const tienePermiso = (modulo, funcion) =>
        permisos.some((p) => p.modulo === modulo && p.funcion === funcion);

    // Modales
    const [tankModal, setTankModal] = useState({ open: false, tanque: null });
    const [stripModal, setStripModal] = useState({ open: false, tipo: null });
    const [alarmasOpen, setAlarmasOpen] = useState(false);
    const [configOpen, setConfigOpen] = useState(false);

    const apiGet = useCallback(async (path) => {
        const r = await fetch(`${apiUrl}/api/plantas-iot${path}`, {
            headers: { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
    }, [apiUrl]);

    // ── Permisos del usuario ───────────────────────────────────────────────────
    useEffect(() => {
        fetchPermisos().then(setPermisos).catch(() => setPermisos([]));
    }, []);

    // ── Lista de plantas ───────────────────────────────────────────────────────
    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            try {
                const ps = await apiGet('/plantas');
                if (!activo) return;
                setPlantas(ps);
                // Si la planta elegida ya no está (p. ej. se desactivó), pasar a la primera
                setPlantaId((prev) => (ps.some((p) => p.id === prev) ? prev : (ps[0] ? ps[0].id : '')));
                setErrorServidor(false);
            } catch {
                if (activo) { setErrorServidor(true); setCargando(false); }
            }
        };
        cargar();
        const iv = setInterval(cargar, POLL_MS);
        return () => { activo = false; clearInterval(iv); };
    }, [apiGet, recarga]);

    // ── Lecturas y alarmas de la planta seleccionada ───────────────────────────
    useEffect(() => {
        if (!plantaId) return;
        let activo = true;
        const cargar = async () => {
            try {
                const hasta = new Date();
                const desde = new Date(hasta.getTime() - 24 * 3600 * 1000);
                const [lat, ev] = await Promise.all([
                    apiGet(`/${plantaId}/latest`),
                    apiGet(`/${plantaId}/eventos?from=${desde.toISOString()}&to=${hasta.toISOString()}&limit=1000`),
                ]);
                if (!activo) return;
                // El nombre visible configurado reemplaza al del equipo en toda la pantalla
                setTanques(lat.map((t) => ({ ...t, tanque_equipo: t.tanque, tanque: t.nombre || t.tanque })));
                setAlarmas24h(ev.total);
                setLastUpdate(new Date());
                setErrorServidor(false);
            } catch {
                if (activo) setErrorServidor(true);
            } finally {
                if (activo) setCargando(false);
            }
        };
        setCargando(true);
        cargar();
        const iv = setInterval(cargar, POLL_MS);
        return () => { activo = false; clearInterval(iv); };
    }, [plantaId, apiGet, recarga]);

    const planta = plantas.find((p) => p.id === plantaId);
    const plantaOnline = !!(planta && planta.online);

    // ── Resumen ────────────────────────────────────────────────────────────────
    const conPct = tanques.filter((t) => t.volumen_pct !== null);
    const nivelPromedio = conPct.length
        ? conPct.reduce((a, t) => a + t.volumen_pct, 0) / conPct.length
        : null;

    const tnPorProducto = tanques.reduce((acc, t) => {
        if (t.masa_kg === null) return acc;
        const prod = (t.producto || 'otro').toLowerCase();
        acc[prod] = (acc[prod] || 0) + t.masa_kg / 1000;
        return acc;
    }, {});
    const tnTotal = Object.values(tnPorProducto).reduce((a, b) => a + b, 0);

    // Kg envasables por producto (suma tanque por tanque, cada uno sobre su propio piso)
    const kgEnvasablePorProducto = tanques.reduce((acc, t) => {
        const prod = (t.producto || 'otro').toLowerCase();
        acc[prod] = (acc[prod] || 0) + kgEnvasables(t);
        return acc;
    }, {});

    const tanquesDe = (prod) =>
        tanques.filter((t) => (t.producto || '').toLowerCase() === prod).map((t) => t.tanque).join(' · ') || '—';

    // "4.378 × 10 kg · 2.919 × 15 kg (sobre 10%)" — envases alternativos con lo envasable
    const envasesDe = (prod) => {
        const kg = kgEnvasablePorProducto[prod] || 0;
        const texto = (ENVASES_KG[prod] || [])
            .map((peso) => `${fmtNum(Math.floor(kg / peso), 0)} × ${peso} kg`)
            .join(' · ');
        return `${texto} (sobre ${PISO_ENVASADO_PCT}%)`;
    };

    const stripBoxes = [
        { label: 'Nivel promedio', val: `${fmtNum(nivelPromedio, 0)}%`, sub: 'todos los tanques', color: 'info.main', tipo: 'nivel' },
        { label: 'Propano (tn)', val: fmtNum(tnPorProducto.propano || 0, 1), envases: envasesDe('propano'), sub: tanquesDe('propano'), color: COLOR_PRODUCTO.propano, tipo: 'propano' },
        { label: 'Butano (tn)', val: fmtNum(tnPorProducto.butano || 0, 1), envases: envasesDe('butano'), sub: tanquesDe('butano'), color: COLOR_PRODUCTO.butano, tipo: 'butano' },
        { label: 'Total disponible (tn)', val: fmtNum(tnTotal, 1), sub: 'todos los productos', color: 'success.main', tipo: 'total' },
        {
            label: 'Alarmas 24 h', val: alarmas24h,
            sub: alarmas24h === 0 ? 'sin alarmas · ver historial' : 'últimas 24 h · ver historial',
            color: alarmas24h > 0 ? 'error.main' : 'success.main', tipo: 'alarmas',
        },
    ];

    function abrirStrip(tipo) {
        if (!tipo) return;
        if (tipo === 'alarmas') setAlarmasOpen(true);
        else setStripModal({ open: true, tipo });
    }

    function cerrarConfig(huboCambios) {
        setConfigOpen(false);
        if (huboCambios) setRecarga((n) => n + 1);
    }

    // ── Estado de conexión ─────────────────────────────────────────────────────
    let chipEstado;
    if (errorServidor) {
        chipEstado = { label: 'Sin conexión al servidor', color: 'error', icon: <CloudOffIcon sx={{ fontSize: '12px !important' }} /> };
    } else if (plantaOnline) {
        chipEstado = { label: 'Planta en línea', color: 'success', icon: <WifiIcon sx={{ fontSize: '12px !important' }} /> };
    } else {
        const min = planta ? planta.minutos_sin_datos : null;
        chipEstado = {
            label: min !== null && min !== undefined ? `Sin datos hace ${min} min` : 'Sin datos',
            color: 'warning',
            icon: <WifiOffIcon sx={{ fontSize: '12px !important' }} />,
        };
    }

    return (
        <>
            <Navbar />
            <Box sx={{ mt: '10px', mb: '60px', bgcolor: 'background.default', minHeight: '100%' }}>

                {/* HEADER */}
                <Paper elevation={0} sx={{ px: 2, py: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, borderBottom: 1, borderColor: 'divider', borderRadius: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WaterIcon sx={{ color: '#fff', fontSize: 16 }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} fontSize={13} lineHeight={1.2}>Plantas IoT</Typography>
                            <Typography variant="caption" color="text.disabled" fontSize={10} lineHeight={1}>plantas_iot</Typography>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <Select
                                value={plantaId}
                                onChange={(e) => setPlantaId(e.target.value)}
                                displayEmpty
                                sx={{ fontSize: 13 }}
                            >
                                {plantas.length === 0 && <MenuItem value="" disabled>Sin plantas</MenuItem>}
                                {plantas.map((p) => (
                                    <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>{p.nombre}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {!isMobile && (
                            <>
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
                                    <ToggleButton value="realtime" sx={{ px: 1.5, gap: 0.5, fontSize: 11, py: 0.5 }}>
                                        <DashboardIcon sx={{ fontSize: 14 }} /> Tiempo real
                                    </ToggleButton>
                                    <ToggleButton value="history" sx={{ px: 1.5, gap: 0.5, fontSize: 11, py: 0.5 }}>
                                        <BarChartIcon sx={{ fontSize: 14 }} /> Histórico
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </>
                        )}
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                            size="small"
                            icon={chipEstado.icon}
                            label={chipEstado.label}
                            color={chipEstado.color}
                            variant="outlined"
                            sx={{ fontSize: 10, height: 22 }}
                        />
                        {tienePermiso('PlantasIot', 'configuracion') && (
                            <Tooltip title="Configuración">
                                <IconButton size="small" onClick={() => setConfigOpen(true)}>
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!isMobile && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11 }}>
                                {lastUpdate ? lastUpdate.toLocaleTimeString('es-AR', { hour12: false }) : '--:--:--'}
                            </Typography>
                        )}
                    </Stack>
                </Paper>

                {isMobile && (
                    <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={view} onChange={(_, v) => setView(v)} variant="fullWidth">
                            <Tab value="realtime" label="Tiempo real" sx={{ fontSize: 11 }} />
                            <Tab value="history" label="Histórico" sx={{ fontSize: 11 }} />
                        </Tabs>
                    </Paper>
                )}

                <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1200, mx: 'auto' }}>

                    {planta && planta.ultimo_envio && (
                        <Typography variant="caption" color="text.disabled" display="block" mb={1.5}>
                            Último envío de la planta: {fmtFecha(planta.ultimo_envio)}
                        </Typography>
                    )}

                    {view === 'history' ? (
                        plantaId
                            ? <PlantasIotHistoryPanel plantaId={plantaId} tanques={tanques} apiUrl={apiUrl} />
                            : <Alert severity="info">Seleccioná una planta.</Alert>
                    ) : cargando && tanques.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : errorServidor && tanques.length === 0 ? (
                        <Alert severity="error">No se pudo conectar con el servidor. Se reintentará automáticamente.</Alert>
                    ) : tanques.length === 0 ? (
                        <Alert severity="info">Esta planta todavía no envió lecturas.</Alert>
                    ) : (
                        <>
                            {/* STRIP — clickeable para abrir historial */}
                            <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
                                {stripBoxes.map((s, i) => (
                                    <Grid item xs={6} sm={4} md key={i} sx={{ display: 'flex' }}>
                                        <Paper
                                            variant="outlined"
                                            onClick={() => abrirStrip(s.tipo)}
                                            sx={{
                                                p: 1.5, pl: 2, width: '100%',
                                                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                position: 'relative',
                                                cursor: s.tipo ? 'pointer' : 'default',
                                                transition: 'all 0.15s',
                                                '&:hover': s.tipo ? { borderColor: s.color, transform: 'translateY(-1px)' } : {},
                                            }}
                                        >
                                            <Typography variant="caption" sx={{
                                                position: 'absolute', top: -9, left: 12,
                                                bgcolor: 'background.paper', px: 0.8,
                                                fontSize: 9, fontWeight: 600, color: 'text.disabled',
                                                textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap',
                                            }}>
                                                {s.label}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Orbitron', sans-serif", color: s.color, fontWeight: 700, fontSize: { xs: 18, sm: 22 }, lineHeight: 1.2 }}>
                                                {s.val}
                                            </Typography>
                                            {s.envases && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 600, mt: 0.3, lineHeight: 1.3 }}>
                                                    {s.envases}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9, mt: 0.2 }}>
                                                {s.sub}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* TANQUES — click abre historial */}
                            <Grid container spacing={2}>
                                {tanques.map((t) => (
                                    <Grid item xs={12} md={6} key={t.tanque_id}>
                                        <PlantasIotTankCard
                                            t={t}
                                            plantaOnline={plantaOnline}
                                            onClick={() => setTankModal({ open: true, tanque: t })}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Box>

                {/* Modal historial de tanque */}
                <PlantasIotTankHistoryModal
                    open={tankModal.open}
                    tanque={tankModal.tanque}
                    plantaId={plantaId}
                    color={tankModal.tanque ? colorProducto(tankModal.tanque.producto) : undefined}
                    apiUrl={apiUrl}
                    onClose={() => setTankModal({ open: false, tanque: null })}
                />

                {/* Modal historial del strip */}
                <PlantasIotStripHistoryModal
                    open={stripModal.open}
                    tipo={stripModal.tipo}
                    tanques={tanques}
                    plantaId={plantaId}
                    apiUrl={apiUrl}
                    onClose={() => setStripModal({ open: false, tipo: null })}
                />

                {/* Modal historial de alarmas */}
                <PlantasIotAlarmasModal
                    open={alarmasOpen}
                    plantaId={plantaId}
                    tanques={tanques}
                    apiUrl={apiUrl}
                    onClose={() => setAlarmasOpen(false)}
                />

                {/* Configuración (solo con permiso) */}
                {tienePermiso('PlantasIot', 'configuracion') && (
                    <PlantasIotConfigDialog
                        open={configOpen}
                        apiUrl={apiUrl}
                        onClose={cerrarConfig}
                    />
                )}
            </Box>
        </>
    );
}